import React, { FC, useEffect, useMemo } from 'react';
import { DesktopWidget } from './desktop/DesktopWidget';
import { ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { createApolloClient } from './apollo/client';
import { useEnvironment, WidgetProps } from '@wix/yoshi-flow-editor';
import { StyleParamsCSSVariables } from '../old/components/style-params-css-variables/StyleParamsCSSVariables';
import { MobileWidget } from './mobile/MobileWidget';
import { FetchAdapter } from './worker/fetch/fetchAdapter';
import { FetchModuleProps } from './worker/fetch/fetchModule';
import { useChannelId } from './hooks';
import { Channel } from './common/components/Channel/Channel';
import { SingleVideo } from './common/components/SingleVideo/SingleVideo';
import { WorkerHandlersContext } from './hooks/useWorkerHandlers';
import { HandlersProps } from './worker/handlers';

export type ControllerProps = {
  gqlHost: string;
  instance: string;
  initialCacheData?: NormalizedCacheObject;
} & FetchModuleProps &
  HandlersProps;

export type NewWidgetProps = WidgetProps<ControllerProps>;

export const NewWidget: FC<NewWidgetProps> = (props) => {
  const {
    instance,
    initialCacheData,
    gqlHost,
    doPost,
    responses,
    removeResponse,
    functions,
    state,
  } = props;
  const channelId = useChannelId();

  const fetchAdapter = useMemo(
    () => new FetchAdapter(instance, doPost, removeResponse),
    [instance, doPost, removeResponse],
  );

  // when a user does a sign in/out the instance changes and the client will be recreated.
  // the new client will refetch everything if initialCacheData is cleared
  const client = useMemo(
    () => createApolloClient(gqlHost, instance, fetchAdapter, initialCacheData),
    [instance, initialCacheData, gqlHost, fetchAdapter],
  );

  const { isMobile } = useEnvironment();

  const widget = useMemo(
    () => (isMobile ? <MobileWidget /> : <DesktopWidget />),
    [isMobile],
  );

  useEffect(() => {
    fetchAdapter.setResponses(responses);
  }, [fetchAdapter, responses]);

  return (
    <ApolloProvider client={client}>
      <WorkerHandlersContext.Provider value={{ functions, state }}>
        <StyleParamsCSSVariables>
          {channelId ? (
            <Channel>{widget}</Channel>
          ) : (
            <SingleVideo>{widget}</SingleVideo>
          )}
        </StyleParamsCSSVariables>
      </WorkerHandlersContext.Provider>
    </ApolloProvider>
  );
};
